import React, { useState, useEffect, useCallback } from 'react';
import { AnnouncementData } from '../store/Announcement/types';
import { useAuth } from '../contexts/Auth';
import { PopUpAnnouncement } from '../components/Modals/PopUpAnnouncement/popUpAnnouncement';
import { useGetAnnouncementsShopmanager } from '../store/Announcement';
import { EnRoles } from '../interfaces/Roles';
import { useUpdateAnnouncement } from '../store/Announcement/ShowAnnouncement/shopmanager';

interface AnnouncementProviderProps {
    children: React.ReactNode;
}

const AnnouncementProvider: React.FC<AnnouncementProviderProps> = ({
    children,
}) => {
    const [dataModal, setDataModal] = useState<AnnouncementData>(
        {} as AnnouncementData,
    );
    const [open, setOpen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [shownIds, setShownIds] = useState<number[]>([]);
    const [closedIds, setClosedIds] = useState<number[]>([]);

    const skipAnnouncementsUntil = localStorage.getItem(
        '@NeerHouse:skipAnnouncementsUntil',
    );
    const isSkippingAnnouncements = skipAnnouncementsUntil
        ? new Date().getTime() < parseInt(skipAnnouncementsUntil, 10)
        : false;

    const { data: authData, isAssuming } = useAuth();
    const { mutateAsync: markAsRead } = useUpdateAnnouncement();

    const isShopManager = authData?.role.some(
        (role: { type: string }) => role.type === EnRoles.shopManager,
    );

    const { data: announcemments } = useGetAnnouncementsShopmanager({
        'filters[isRead]': false,
        'filters[showPopup]': true,
    });

    const markClosedAsRead = useCallback(
        (ids: number[]) => {
            ids.forEach((id) => {
                markAsRead({ id: String(id) });
            });
        },
        [markAsRead],
    );

    const getAnnouncement = (announcement: AnnouncementData[]) => {
        const filter = announcement.find(
            (el) =>
                el.show_popup &&
                !el.recipients[0].read_at &&
                !shownIds.includes(el.id),
        );

        if (filter && isShopManager && !isAssuming) {
            setDataModal(filter);
            setCurrentIndex(shownIds.length);
            setShownIds((prev) => [...prev, filter.id]);
            setOpen(true);
        } else if (closedIds.length > 0) {
            markClosedAsRead(closedIds);
            setClosedIds([]);
        }
    };

    const handleClose = useCallback(() => {
        setClosedIds((prev) => [...prev, dataModal.id]);
        setOpen(false);
    }, [dataModal.id]);

    const handleSkip = useCallback(() => {
        markClosedAsRead(closedIds);
        setClosedIds([]);
        setShownIds([]);
        setCurrentIndex(0);

        const skipUntil = new Date();
        skipUntil.setHours(skipUntil.getHours() + 24);
        localStorage.setItem(
            '@NeerHouse:skipAnnouncementsUntil',
            skipUntil.getTime().toString(),
        );
        setOpen(false);
    }, [closedIds, dataModal.id, markClosedAsRead]);

    useEffect(() => {
        if (announcemments?.data.length && !open && !isSkippingAnnouncements) {
            getAnnouncement(announcemments.data);
        }
    }, [announcemments?.data, isShopManager, open]);

    return (
        <>
            {isShopManager &&
                open &&
                !isAssuming &&
                !isSkippingAnnouncements && (
                    <PopUpAnnouncement
                        dataModal={dataModal}
                        openModal={setOpen}
                        onClose={handleClose}
                        onSkip={handleSkip}
                        current={currentIndex}
                        total={announcemments?.meta?.total}
                    />
                )}
            {children}
        </>
    );
};

export default AnnouncementProvider;
